"use client";

/**
 * TiltCard — wraps a cover card in a subtle interactive 3D tilt (pointer-driven
 * parallax). The outer div holds the CSS `perspective`; the inner div rotates
 * toward the pointer and lifts slightly, then eases back on leave. Neither div
 * clips, so the lift is never cropped by a parent's rounded/overflow box.
 * Honours reduced-motion. Pointer-only (no gyro) so it's calm in a scroll list.
 *
 * CoverExplicitSticker — the Parental Advisory sticker overlaid bottom-left on a
 * cover thumbnail when the item is explicit. Drop it inside the (relative) cover
 * box; renders nothing when not explicit.
 */

import React, { useRef } from "react";
import { cn } from "@/lib/utils";
import { ParentalAdvisoryLabel } from "@/components/ui/parental-advisory-label";
import { AiLabelIcon } from "@/components/ui/ai-label-icon";
import { aiLevelMeta, type AILevel } from "@/data/ai-disclosure";

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function TiltCard({
  children,
  className,
  max = 9,
  scale = 1.03,
}: {
  children: React.ReactNode;
  className?: string;
  /** max tilt in degrees */
  max?: number;
  scale?: number;
}) {
  const inner = useRef<HTMLDivElement>(null);
  const raf = useRef(0);

  const set = (rx: number, ry: number, s: number) => {
    const el = inner.current;
    if (!el) return;
    el.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(${s})`;
  };
  const onMove = (e: React.PointerEvent) => {
    if (REDUCED) return;
    const el = inner.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const ry = ((e.clientX - r.left) / r.width - 0.5) * 2 * max;
    const rx = -((e.clientY - r.top) / r.height - 0.5) * 2 * max;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => set(rx, ry, scale));
  };
  const reset = () => {
    cancelAnimationFrame(raf.current);
    set(0, 0, 1);
  };

  return (
    <div className={className} style={{ perspective: 800 }}>
      <div
        ref={inner}
        onPointerMove={onMove}
        onPointerLeave={reset}
        style={{
          height: "100%",
          transition: "transform 160ms ease-out",
          // NOT preserve-3d: a flat transform tilts just the same under the
          // parent perspective, and preserve-3d + children with filters/
          // backdrop-filters triggers Chrome's partial render/hit-test bug
          // (play buttons responding only on their bottom half mid-tilt).
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function CoverExplicitSticker({
  explicit,
  className,
}: {
  explicit?: boolean;
  className?: string;
}) {
  if (!explicit) return null;
  // Standard placement on EVERY thumbnail: bottom-LEFT corner, inset from the
  // edges (play button owns the bottom-RIGHT corner). 25% smaller than v1.
  // (The AI mark owns the TOP-LEFT corner — see CoverAiSticker below.)
  return (
    <div
      className={cn(
        "absolute bottom-2 left-2 z-10 w-[30%] max-w-[63px] min-w-[26px] pointer-events-none",
        className
      )}
    >
      <ParentalAdvisoryLabel width={63} className="w-full h-auto drop-shadow-md" />
    </div>
  );
}

/**
 * CoverAiSticker — the recording-industry standard AI mark, top-LEFT corner
 * (Gordon 2026-10-31: AI top left, parental advisory bottom left, play bottom
 * right). Takes either label format — the disclosure's ai-generated /
 * ai-assisted or the badge scale's ai / partly_ai / hybrid.
 * of a thumbnail, shown when the work/release declares an AI level.
 * Solid mark for ai-generated; slightly translucent for ai-assisted.
 * Drop it inside the (relative) cover box; renders nothing for human-made.
 */
export function CoverAiSticker({
  level: raw,
  className,
}: {
  level?: AILevel | string | null;
  className?: string;
}) {
  const l = String(raw || "").toLowerCase();
  const level: AILevel | null = l === "ai-generated" || l === "ai" ? "ai-generated"
    : l === "ai-assisted" || l === "hybrid" || l === "partly_ai" ? "ai-assisted" : null;
  if (!level) return null;
  const meta = aiLevelMeta(level);
  return (
    <div
      className={cn(
        "absolute top-2 left-2 z-10 pointer-events-none drop-shadow-md",
        level === "ai-assisted" && "opacity-80",
        className
      )}
      title={`${meta.industryLabel} — ${meta.industryText}`}
    >
      <AiLabelIcon size={22} className="rounded-[5px] ring-1 ring-white/60" title={meta.industryLabel} />
    </div>
  );
}
